import React, {
  ChangeEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Fade, Link, Popper, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useController, useForm } from "react-hook-form";
import { useDebounce } from "use-debounce";
import { useNavigate } from "react-router";
import {
  InvestmentTypeEnum,
  useSearchSymbolV2,
  SymbolV2,
} from "@rentablo/api/apiv1";
import InfoIcon from "@icons/myicons/info.svg?react";
import { RHFCheckbox, SearchField } from "@rentablo/components";
import { Banner } from "@rentablo/components";
import { SecuritiesSearchResults } from "@features/transactionWizard/components/SecuritySearch/SecuritiesSearchResults";
import { useSecurityImages } from "../../../../hooks/useSecurityImages";

type FormFields = {
  search: string;
  all: boolean;
} & FilterFields;

type FilterFields = {
  "11_stock": boolean;
  "21_fund": boolean;
  "31_bond": boolean;
  "41_cash": boolean;
  "51_certos": boolean;
  "61_pmetals": boolean;
  "71_massets": boolean;
  "81_fcurr": boolean;
  "82_crypto": boolean;
  "22_etf": boolean;
  "91_managed": boolean;
};

const searchFilters: (keyof FilterFields)[] = [
  "11_stock",
  "21_fund",
  "31_bond",
  "41_cash",
  "51_certos",
  "61_pmetals",
  "71_massets",
  "81_fcurr",
  "82_crypto",
  "22_etf",
  "91_managed",
];

type Props = {
  onSymbolSelect: (symbol: SymbolV2) => void;
  defaultSearchFilter?: InvestmentTypeEnum;
  defaultSearchValue?: string;
  showManual?: boolean;
  depotId?: number;
};

export const SecuritiesSearch = ({
  onSymbolSelect,
  defaultSearchValue,
  defaultSearchFilter,
  showManual = false,
  depotId,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const ref = useRef<HTMLInputElement>(null);
  const searchResultsRef = useRef<HTMLDivElement>(null);
  const [areSearchResultsOpen, setAreSearchResultsOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchResultsHeight, setSearchResultsHeight] = useState(0);
  const { control, watch, setValue } = useForm<FormFields>({
    defaultValues: {
      search: defaultSearchValue ?? "",
      all: defaultSearchFilter ? false : true,
      ...searchFilters.reduce(
        (acc, filter) => {
          acc[filter] = filter === defaultSearchFilter ? true : false;
          return acc;
        },
        {} as Record<keyof FilterFields, boolean>
      ),
    },
  });

  const { field } = useController({ control, name: "search" });
  const values = watch();

  const type = useMemo(() => {
    return searchFilters.filter((filter) => values[filter]);
  }, [values]);
  const [searchValue] = useDebounce(values.search, 300);

  const { data: searchResults, isFetching } = useSearchSymbolV2(
    {
      search: searchValue,
      type,
    },
    {
      enabled: searchValue.length >= 3,
    }
  );

  const logos = useSecurityImages(
    searchResults?.symbols?.map((symbol) => ({
      isin: symbol.isin,
      tickerSymbol: symbol.quoteProvider === "crypto" ? symbol.id : undefined,
      type: symbol.type,
      hasValidIsin: true,
      quoteProvider: symbol.quoteProvider,
    })) || []
  );

  useEffect(() => {
    if (searchValue.length >= 3) {
      setAreSearchResultsOpen(true);
    } else {
      setAreSearchResultsOpen(false);
    }
  }, [searchValue]);

  useEffect(() => {
    setAnchorEl(ref?.current);
  }, [ref]);

  useEffect(() => {
    if (!searchResultsRef?.current) return;

    const resizeObserver = new ResizeObserver(() => {
      setSearchResultsHeight(searchResultsRef?.current?.offsetHeight || 0);
    });
    resizeObserver.observe(searchResultsRef.current);

    return () => resizeObserver.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchResultsRef.current]);

  const openManualWizard = () => {
    const params = [
      ["type", type],
      ["depotId", depotId !== undefined ? String(depotId) : undefined],
    ].filter(([_, value]) => Boolean(value)) as [string, string][];
    const string = new URLSearchParams(params).toString();

    navigate(`/manual-transaction-wizard${string.length ? `?${string}` : ""}`);
  };

  return (
    <>
      <Box width="100%" position="relative">
        <SearchField
          placeholder={t("securitiesSearch.placeholder")}
          fullWidth
          inputProps={{ "data-testid": "symbol-search-input" }}
          sx={{
            width: "100%",
            marginBottom: (theme) => theme.spacing(5),
          }}
          size="small"
          value={field.value}
          onChange={field.onChange}
          name="securities-search"
          ref={ref}
          autoFocus
        />
        <Box
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          rowGap={2}
          mt={searchResultsHeight / 4}
          px={3}
        >
          <RHFCheckbox
            name="all"
            key="all"
            label={t("securitiesSearch.all")}
            control={control}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              if (!event.target.checked) return;

              const someChecked = searchFilters.some(
                (filter) => values[filter]
              );
              if (someChecked) {
                searchFilters.forEach((filter) => {
                  setValue(filter, false, { shouldValidate: true });
                });
                setValue("all", true, { shouldValidate: true });
              }
            }}
          />
          {searchFilters.map((filter) => (
            <RHFCheckbox
              name={filter}
              key={filter}
              label={t(`investmentTypeClassification.types.${filter}`)}
              control={control}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                const someChecked = searchFilters.some(
                  (filter) => filter !== event.target.name && values[filter]
                );
                if (someChecked) {
                  setValue(
                    event.target.name as keyof FilterFields,
                    event.target.checked,
                    { shouldValidate: true }
                  );
                } else {
                  setValue("all", !event.target.checked, {
                    shouldValidate: true,
                  });
                  setValue(
                    event.target.name as keyof FilterFields,
                    event.target.checked,
                    {
                      shouldValidate: true,
                    }
                  );
                }
              }}
            />
          ))}
        </Box>
        {anchorEl && (
          <Popper
            open={areSearchResultsOpen}
            anchorEl={anchorEl}
            placement="bottom"
            disablePortal
            transition
            sx={{
              display: "flex",
              width: "100%",
            }}
          >
            {({ TransitionProps }) => (
              <Fade
                {...TransitionProps}
                addEndListener={() => {
                  setSearchResultsHeight(
                    searchResultsRef?.current?.offsetHeight || 0
                  );
                }}
              >
                <Box maxHeight="100%" width="100%" ref={searchResultsRef}>
                  <SecuritiesSearchResults
                    symbols={searchResults?.symbols}
                    isFetching={isFetching}
                    onSymbolSelect={onSymbolSelect}
                    logos={logos}
                  />
                </Box>
              </Fade>
            )}
          </Popper>
        )}
      </Box>
      {showManual && (
        <Banner
          type="grey-info"
          mt={6}
          icon={<InfoIcon />}
          width="100%"
          text={
            <Box display="flex" gap={1}>
              <Typography variant="body3">
                {t("symbolSearch.manualInvestmentLabel")}
              </Typography>
              <Link
                data-testid="create-manual-investment"
                variant="body3"
                color="primary"
                style={{ cursor: "pointer" }}
                onClick={openManualWizard}
              >
                {t("symbolSearch.manualInvestment")}
              </Link>
            </Box>
          }
        />
      )}
    </>
  );
};
